import { connect } from "react-redux";
import {
  StyleSheet,
  Document,
  Page,
  View,
  Text,
  Image as ReactPdfImage,
  Font,
  usePDF,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import { FC, useEffect } from "react";

import { convert, getFormData, getStatusTextAndColor } from "app/helpers/helperFunction";
import { saveBillPdf } from "app/reducers/billing/billingAction";
import { useAuth } from "../../auth";
import { underline } from "pdfkit";
import { TRANSACTION_TYPE } from "app/helpers/constants";

export interface billTasks {
  TaskCode: string;
  ClientName: string;
  TypeName: string;
  CategoryName: string;
  Fees: number;
  BillId: number;
  Id: number;
}

export interface billPayments {
  Type: number;
  Amount: number;
  Narration: string;
  TaskCode: string;
}

interface billData {
  IsReimbursementInvoice: boolean | undefined;
  IsProformaInvoice: boolean | undefined;
  base64Qr: string;
  base64Sign: string;
  Date: string;
  Year: string;
  TotalAmountWithoutTax: number;
  TotalTaxAmount: number;
  TotalAmount: number;
  TaxRate: number;
  BillNumber: string;
  ClientName: string;
  Client_GSTIN: string;
  LegalStatus?: string;
  Address?: string;
  State?: string;
  Tasks: Array<billTasks>;
  Payments: Array<billPayments>;
  Receipts: Array<billPayments>;
  PaymentStatus: number;
  Firm: {
    Address: string;
    IFSCCode: string;
    BankName: string;
    AccountNumber: string;
    Name: string;
    PANNumber: string;
    HasGST: boolean;
    GSTIN: string;
    IsCAFirm: boolean;
    PersonName: string;
    Designation: string;
    QRCodeFile: {
      FileName: string;
      FilePath: string;
      FileUrl: string;
      MimeType: string;
    };
    SignatureFile: {
      FileName: string;
      FilePath: string;
      FileUrl: string;
      MimeType: string;
    };
  };
  Id: number;
}

interface props {
  billData: billData;
  saveBillPdfDispatch: Function;
}

interface generatePdfClickProps {
  billData: billData;
  title: string;
  showIcon?: boolean;
  className: string;
}

// Register Roboto font
Font.register({
  family: "Roboto",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
});

interface formatedBillProps {
  title?: string;
  //companyName: string | undefined;
  billData: {
    IsReimbursementInvoice: boolean | undefined;
    IsProformaInvoice: boolean | undefined;
    base64Qr: string;
    base64Sign: string;
    Date: string;
    Year: string;
    TaxRate: number;
    TotalAmountWithoutTax: number;
    TotalTaxAmount: number;
    TotalAmount: number;
    BillNumber: string;
    ClientName: string;
    Client_GSTIN: string;
    LegalStatus?: string;
    Address?: string;
    State?: string;
    Tasks: Array<billTasks>;
    Payments: Array<billPayments>;
    Receipts: Array<billPayments>;
    PaymentStatus: number;
    Firm: {
      Address: string;
      IFSCCode: string;
      BankName: string;
      AccountNumber: string;
      Name: string;
      PANNumber: string;
      HasGST: boolean;
      GSTIN: string;
      IsCAFirm: boolean;
      PersonName: string;
      Designation: string;
      QRCodeFile: {
        FileName: string;
        FilePath: string;
        FileUrl: string;
        MimeType: string;
      };
      SignatureFile: {
        FileName: string;
        FilePath: string;
        FileUrl: string;
        MimeType: string;
      };
    };
    Id: number;
  };
}

const GetBillPdf: FC<formatedBillProps> = ({ billData, title }) => {
  const { text, color } = getStatusTextAndColor(billData.PaymentStatus);
  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.pageContainer}>
          {/* Header Section */}
          <View style={styles.header}>
            <View style={styles.headerTop}>
              <View style={styles.leftHeader}>
                <Text style={styles.companyName}>{billData.Firm.Name}</Text>
                {billData.Firm.IsCAFirm && (
                  <Text style={styles.CA}>Chartered Accountants</Text>
                )}
                <Text style={styles.companyDetails}>
                  {billData.Firm.Address}
                </Text>
                {billData.Firm.HasGST && (
                  <Text style={styles.companyDetails}>
                    GSTIN: {billData.Firm.GSTIN}
                  </Text>
                )}
              </View>
              <View style={styles.rightHeader}>
                <Text style={styles.invoiceTitle}>
                  {title ||
                    (billData.IsReimbursementInvoice
                      ? "Reimbursement Challan"
                      : "Tax Invoice")}
                </Text>
                <Text style={{...styles.invoiceInfo, color}}>
                  {text}
                </Text>
                <Text style={styles.invoiceInfo}>
                  {title === "Proforma Invoice"
                    ? "Proforma Invoice No. : "
                    : billData.IsReimbursementInvoice
                    ? "Reimbursement Challan No. : "
                    : "Invoice No. : "}{" "}
                  {new Date().getMonth() + 1 < 3
                    ? `${new Date().getFullYear() - 1}-${String(
                        new Date().getFullYear()
                      ).slice(-2)}`
                    : `${new Date().getFullYear()}-${String(
                        new Date().getFullYear() + 1
                      ).slice(-2)}`}
                  /{billData.BillNumber}
                </Text>
                <Text style={styles.invoiceInfo}>
                  Date: {billData.Date ? convert(billData.Date) : "-"}
                </Text>
              </View>
            </View>
          </View>

          {/* Bill To Section */}
          <View style={styles.billSection}>
            <View style={styles.billTo}>
              <Text style={styles.billToTitle}>Bill To</Text>
              <Text style={styles.billToContent}>{billData.ClientName}</Text>
              {billData.Client_GSTIN && (
                <Text style={styles.billToContent}>
                  GSTIN: {billData.Client_GSTIN}
                </Text>
              )}
              <Text style={styles.billToContent}>
                {billData.Address
                  ? `${billData.Address}, ${billData.State}`
                  : "-"}
              </Text>
            </View>
          </View>

          {/* Table Section */}
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableDescPart}>Description</Text>
              <Text style={styles.tableAmountPart}>Amount</Text>
            </View>
            {!billData.IsReimbursementInvoice &&
              billData?.Tasks?.map((task, index) => (
                <View style={styles.singleTableRow} key={index}>
                  <Text style={styles.rowDescPart}>{task.TypeName}</Text>
                  <Text style={styles.rowAmountPart}>{task.Fees}</Text>
                </View>
              ))}
            {billData.IsReimbursementInvoice &&
              billData?.Payments?.map(
                (payment, index) =>
                  (payment.Type === 5 || payment.Type === 3) && (
                    <View style={styles.singleTableRow} key={index}>
                      <Text style={styles.rowDescPart}>
                        {payment.Type === 5
                          ? "Reimbursement"
                          : "Govt. Fees/Tax"}
                        {payment.Narration ? ` - ${payment.Narration}` : ""}
                      </Text>
                      <Text style={styles.rowAmountPart}>{payment.Amount}</Text>
                    </View>
                  )
              )}

            {/* Totals Section */}
            <View style={styles.tableTotalSection}>
              {(billData.IsProformaInvoice ||
                (!billData.IsProformaInvoice &&
                  !billData.IsReimbursementInvoice)) &&
                billData.Firm.HasGST && (
                  <View style={styles.borderBottom}>
                    <View style={styles.totalRow}>
                      <Text style={styles.totalLabel}>Subtotal</Text>
                      <Text style={styles.totalAmount}>
                        {billData?.TotalAmountWithoutTax || "0"}
                      </Text>
                    </View>
                    {(billData.State === "Gujarat" ||
                      billData.State === "") && (
                      <>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            SGST @ {billData?.TaxRate / 2}%
                          </Text>
                          <Text style={styles.totalAmount}>
                            {billData?.TotalTaxAmount / 2 || "0"}
                          </Text>
                        </View>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            CGST @ {billData?.TaxRate / 2}%
                          </Text>
                          <Text style={styles.totalAmount}>
                            {billData?.TotalTaxAmount / 2 || "0"}
                          </Text>
                        </View>
                      </>
                    )}
                    {billData.State !== "Gujarat" && billData.State !== "" && (
                      <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>
                          IGST @ {billData?.TaxRate}%
                        </Text>
                        <Text style={styles.totalAmount}>
                          {billData?.TotalTaxAmount || "0"}
                        </Text>
                      </View>
                    )}
                  </View>
                )}
              <View style={styles.finalTotal}>
                <Text style={styles.finalTotalLabel}>Total Amount</Text>
                <Text style={styles.finalTotalAmount}>
                  {!billData.IsReimbursementInvoice
                    ? billData?.TotalAmount || "0"
                    : billData?.TotalAmount}
                </Text>
              </View>
            </View>
          </View>

          {/* Footer Section */}
          <View style={styles.footer}>
            <View style={styles.bankDetails}>
              <View style={styles.bankInfo}>
                <Text style={styles.bankInfoTitle}>Bank Details</Text>
                <Text style={styles.bankInfoContent}>{billData.Firm.Name}</Text>
                <Text style={styles.bankInfoContent}>
                  {String(billData?.Firm?.BankName || '')}
                </Text>
                <Text style={styles.bankInfoContent}>
                  Current A/c. No. {billData.Firm.AccountNumber}
                </Text>
                <Text style={styles.bankInfoContent}>
                  IFSC: {billData.Firm.IFSCCode}
                </Text>
                <Text style={styles.noteText}>
                  Please Transfer the amount to our following Bank Account or Send a Cheque
                </Text>
              </View>
              <View style={styles.qrSection}>
                <ReactPdfImage
                  src={`data:image/${billData?.Firm?.QRCodeFile?.MimeType};base64,${billData?.base64Qr}`}
                  style={styles.qrCodeImage}
                />
              </View>
            </View>
            <View style={styles.signatureSection}>
              <Text style={styles.signatureText}>For {billData.Firm.Name}</Text>
              {billData.Firm.IsCAFirm && <Text style={styles.signatureText}>Chartered Accountants</Text>}
              <ReactPdfImage
                style={styles.adminSign}
                src={`data:image/${billData?.Firm?.SignatureFile?.MimeType};base64,${billData?.base64Sign}`}
              />
              <Text style={styles.signatureText}>
                ({billData.Firm.PersonName})
              </Text>
              <Text style={styles.signatureText}>
                {billData.Firm.Designation}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const GetUrlForPdf: FC<props> = ({ billData, saveBillPdfDispatch }) => {
  // const { currentUser } = useAuth();
  // const companyName = currentUser?.CompanyName;

  const [instance] = usePDF({
    document: <GetBillPdf billData={billData} />,
  });

  useEffect(() => {
    if (instance.blob) {
      saveBillPdfDispatch(getFormData({ file: instance.blob }), billData.Id);
      // getBase64String(instance.blob, billData.Id)
    }
  }, [instance.blob]);

  return <></>;
};

export const GeneratePdfByClick: FC<generatePdfClickProps> = ({
  billData,
  title,
  showIcon,
  className,
}) => {
  return (
    <PDFDownloadLink
      document={<GetBillPdf billData={billData} title={title} />}
      fileName={`${billData.BillNumber}.pdf`}
    >
      {() => {
        return (
          <span className={className} title="Download">
            {showIcon ? (
              <i className="fa-solid fa-download"></i>
            ) : (
              <>
                <i className="fa-solid fa-download"></i> Download Pdf
              </>
            )}
          </span>
        );
      }}
    </PDFDownloadLink>
  );
};

export const styles = StyleSheet.create({
  page: {
    backgroundColor: "white",
    fontFamily: "Helvetica",
    padding: 30,
    fontSize: 11,
    color: "#1a1a1a",
    flexDirection: "column",
  },
  pageContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexDirection: "column",
    marginBottom: 10,
    borderBottom: "2px solid #2c5282",
    //paddingBottom: 15,
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  leftHeader: {
    width: "60%",
  },
  rightHeader: {
    width: "40%",
    alignItems: "flex-end",
  },
  companyName: {
    fontSize: 20,
    color: "#2c5282",
    marginBottom: 6,
    fontFamily: "Helvetica-Bold",
  },
  CA: {
    fontSize: 14,
    color: "#4a5568",
    marginBottom: 8,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
  },
  companyDetails: {
    fontSize: 10,
    color: "#4a5568",
    lineHeight: 1.4,
  },
  invoiceTitle: {
    color: "#2c5282",
    fontSize: 18,
    marginBottom: 8,
    textTransform: "uppercase",
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
  },
  invoiceInfo: {
    color: "#4a5568",
    fontSize: 10,
    marginBottom: 4,
    textAlign: "right",
  },
  billSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    backgroundColor: "#f7fafc",
    padding: 15,
    borderRadius: 4,
  },
  billTo: {
    width: "48%",
  },
  billToTitle: {
    fontSize: 11,
    color: "#2c5282",
    marginBottom: 8,
    textTransform: "uppercase",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
  },
  billToContent: {
    fontSize: 10,
    color: "#4a5568",
    lineHeight: 1.4,
  },
  tableContainer: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#2c5282",
    padding: 10,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  tableDescPart: {
    width: "85%",
    fontSize: 10,
    color: "white",
    fontFamily: "Helvetica-Bold",
  },
  tableAmountPart: {
    width: "15%",
    fontSize: 10,
    color: "white",
    textAlign: "right",
    fontFamily: "Helvetica-Bold",
  },
  singleTableRow: {
    flexDirection: "row",
    paddingVertical: 5,
    paddingHorizontal: 10,
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
  },
  rowDescPart: {
    width: "85%",
    fontSize: 10,
    color: "#4a5568",
  },
  rowAmountPart: {
    width: "15%",
    fontSize: 10,
    color: "#4a5568",
    textAlign: "right",
  },
  tableTotalSection: {
    marginLeft: "65%",
    marginTop: 15,
    backgroundColor: "#f7fafc",
    padding: 12,
    borderRadius: 4,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 10,
    color: "#4a5568",
    fontFamily: "Helvetica-Bold",
  },
  totalAmount: {
    fontSize: 10,
    color: "#4a5568",
  },
  finalTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    //borderTop: "2px solid #2c5282",
    marginTop: 3,
  },
  finalTotalLabel: {
    fontSize: 12,
    color: "#2c5282",
    fontFamily: "Helvetica-Bold",
  },
  finalTotalAmount: {
    fontSize: 12,
    color: "#2c5282",
    fontFamily: "Helvetica-Bold",
  },
  footer: {
    marginTop: "auto",
    paddingTop: 20,
    borderTop: "2px solid #2c5282",
  },
  bankDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#f7fafc",
    padding: 15,
    borderRadius: 4,
    marginBottom: 10,
  },
  bankInfo: {
    width: "60%",
  },
  bankInfoTitle: {
    fontSize: 11,
    color: "#2c5282",
    marginBottom: 8,
    textTransform: "uppercase",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
  },
  bankInfoContent: {
    fontSize: 10,
    color: "#4a5568",
    lineHeight: 1.4,
  },
  qrSection: {
    width: "35%",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  qrCodeImage: {
    width: 80,
    height: 80,
  },
  signatureSection: {
    marginTop: 15,
    alignItems: "flex-end",
  },
  adminSign: {
    width: 100,
    marginBottom: 6,
  },
  signatureText: {
    fontSize: 10,
    color: "#4a5568",
    textAlign: "right",
    lineHeight: 1.3,
  },
  noteText: {
    fontSize: 9,
    color: "#718096",
    fontStyle: "italic",
    marginTop: 8,
    textAlign: "center",
  },
  borderBottom: {
    paddingBottom: 3,
    borderBottom: "2px solid #2c5282",
  }
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    saveBillPdfDispatch: (pdfData: FormData, billId: number) =>
      dispatch(saveBillPdf(pdfData, billId)),
  };
};

export default connect(null, mapDispatchToProps)(GetUrlForPdf);